import React, { createContext, useContext, useMemo } from 'react'

import { getToken } from '@helpscout/hsds-tokens/getToken'
import type { GetTokenInput } from '@helpscout/hsds-tokens/getToken'

export type ZIndexContext = 'droplist' | 'sidepanel' | 'modal' | null

export type ZIndexComponent =
  | 'droplist'
  | 'popover'
  | 'tooltip'
  | 'modal'
  | 'overlay'

export const ZIndexStackContext = createContext<string>('0')

export interface UseZIndexOptions {
  zIndex?: number | string
}

export function useZIndex(
  props: UseZIndexOptions,
  component: ZIndexComponent
): number | string | undefined {
  const stackValue = useContext(ZIndexStackContext)

  // 1. Props override everything
  if (props.zIndex !== undefined) {
    return props.zIndex
  }

  // 2. If within a stack context, add component value to stack
  if (stackValue !== '0') {
    const componentToken = getToken(`zindex.${component}` as GetTokenInput)
    if (componentToken !== undefined) {
      return `calc(${stackValue} + ${componentToken})`
    }
  }

  // 3. No parent context - let CSS handle the default
  return undefined
}

export interface ZIndexStackProviderProps {
  context: ZIndexContext
  zIndex?: number | string
  children: React.ReactNode
}

export function ZIndexStackProvider({
  context,
  zIndex,
  children,
}: ZIndexStackProviderProps) {
  const parentValue = useContext(ZIndexStackContext)
  const currentTokenValue =
    zIndex ?? (context ? getToken(`zindex.${context}` as GetTokenInput) : '0')

  // Build calc expression: calc(parent + current)
  const stackedValue = useMemo(() => {
    return parentValue === '0'
      ? String(currentTokenValue)
      : `calc(${parentValue} + ${currentTokenValue})`
  }, [parentValue, currentTokenValue])

  return (
    <ZIndexStackContext.Provider value={stackedValue}>
      {children}
    </ZIndexStackContext.Provider>
  )
}
